import type {MediaSessionController} from '@pexip/media';
import {createMediaSession} from '@pexip/media';
import type {Detach} from '@pexip/signal';

import {logger} from '../logger';
import {infinityClientSignals} from '../signals/InfinityClient.signals';
import {userInitiatedDisconnectSignal} from '../signals/InMeeting.signals';
import {mediaSignals} from '../signals/Media.signals';
import {mediaSessionSignal} from '../signals/MediaSession.signals';
import {remoteStreamSignal} from '../signals/Meeting.signals';
import {getMeetingTitle} from '../utils/getMeetingTitle';
import {combineSubscribes} from '../utils/subscribe';

import type {Infinity} from './InfinityClient.service';
import {mediaService, muteAudio, muteVideo} from './Media.service';

export interface MediaSessionServiceOptions {
    /**
     * Factory creating the session, injectable for testing.
     */
    createSession?: typeof createMediaSession;
    /**
     * Label to present for the ongoing meeting.
     */
    getTitle?: () => string | undefined;
    /**
     * Current local capture mute state.
     */
    getMuteState?: () => {audioMuted: boolean; videoMuted: boolean};
    /**
     * Leave the ongoing meeting.
     */
    leave?: () => void;
    toggleAudioMuted?: () => void;
    toggleVideoMuted?: () => void;
    /**
     * Subscriptions driving the session, each returning a detach function.
     */
    subscribe?: {
        /**
         * The remote meeting media has started being received.
         */
        remoteStream: (handler: () => void) => Detach;
        /**
         * The meeting is over, whether the user left or the call dropped.
         */
        meetingEnded: (handler: () => void) => Detach;
        /**
         * The presented title may have changed.
         */
        title: (handler: () => void) => Detach;
        /**
         * The local capture mute state may have changed.
         */
        muteState: (handler: () => void) => Detach;
    };
}

const defaultGetMuteState = () => ({
    audioMuted: mediaService.media?.audioMuted ?? true,
    videoMuted: mediaService.media?.videoMuted ?? true,
});

const defaultToggleAudioMuted = () => {
    const media = mediaService.media;
    if (media?.audioMuted === undefined) {
        return;
    }
    media.muteAudio(!media.audioMuted);
    muteAudio(media.audioMuted);
};

const defaultToggleVideoMuted = () => {
    const media = mediaService.media;
    if (media?.videoMuted === undefined) {
        return;
    }
    media.muteVideo(!media.videoMuted);
    muteVideo(media.videoMuted);
};

const defaultSubscribe = {
    remoteStream: (handler: () => void) => remoteStreamSignal.add(handler),
    meetingEnded: combineSubscribes(
        handler => infinityClientSignals.onDisconnected.add(handler),
        handler => userInitiatedDisconnectSignal.add(handler),
    ),
    title: (handler: () => void) =>
        infinityClientSignals.onAuthenticatedWithConference.add(handler),
    muteState: combineSubscribes(
        // `onMediaChanged` is the only signal emitted when the `Media` object
        // itself is swapped, which happens after the new tracks have had their
        // mute state restored, so without it the presented state stays stale.
        handler => mediaSignals.onMediaChanged.add(handler),
        handler => mediaSignals.onMediaTrackMuted.add(handler),
        handler => mediaSignals.onMediaTrackStopped.add(handler),
        handler => mediaSignals.onAddTrack.add(handler),
        handler => mediaSignals.onRemoveTrack.add(handler),
    ),
};

/**
 * Present the ongoing meeting to the platform's media controls, e.g. the lock
 * screen, the notification shade or headset buttons.
 *
 * The session exists for as long as the remote meeting media is being
 * received, which is owned here rather than by the view so that it survives the
 * meeting stage being remounted, e.g. on a breakout refer or a transfer.
 *
 * Actions bound to the element playing the remote media, i.e. `play`, `pause`
 * and `enterpictureinpicture`, are not registered here. The view owns the
 * element, so it attaches them with {@link MediaSessionController.registerActions}
 * once the session is published on `mediaSessionSignal`.
 */
export const createMediaSessionService = (
    options: MediaSessionServiceOptions = {},
) => {
    let infinity: Infinity | undefined;
    const {
        createSession = createMediaSession,
        getTitle = () => {
            const meeting = infinity?.getMeeting();
            if (!meeting || !infinity) {
                return undefined;
            }
            return getMeetingTitle({
                serviceType: infinity.getServiceType(),
                conferenceName: meeting.getConferenceName(),
                conferenceAlias: meeting.getConferenceAlias(),
            });
        },
        getMuteState = defaultGetMuteState,
        leave = () => {
            void infinity?.getMeeting().leave();
        },
        toggleAudioMuted = defaultToggleAudioMuted,
        toggleVideoMuted = defaultToggleVideoMuted,
        subscribe = defaultSubscribe,
    } = options;
    let session: MediaSessionController | undefined;
    let detachSession: Detach | undefined;

    const setSession = (updated: MediaSessionController | undefined) => {
        session = updated;
        mediaSessionSignal.emit(updated);
    };

    const syncMuteState = () => {
        if (!session) {
            return;
        }
        const {audioMuted, videoMuted} = getMuteState();
        void session.setMicrophoneActive(!audioMuted);
        void session.setCameraActive(!videoMuted);
    };

    const syncTitle = () => {
        const title = getTitle();
        session?.setMetadata(title ? {title} : undefined);
    };

    const stop = () => {
        if (!session) {
            return;
        }
        logger.debug('Destroy the meeting media session');
        detachSession?.();
        detachSession = undefined;
        session.destroy();
        setSession(undefined);
    };

    const start = () => {
        if (session) {
            return;
        }
        const title = getTitle();
        logger.debug({title}, 'Create the meeting media session');
        const created = createSession({
            metadata: title ? {title} : undefined,
            handlers: {
                // Ref. https://developer.mozilla.org/en-US/docs/Web/API/MediaSession/setActionHandler#browser_compatibility
                // Only Chromium
                hangup: leave,
                // Not Firefox
                togglemicrophone: toggleAudioMuted,
                // Not Firefox
                togglecamera: toggleVideoMuted,
            },
        });
        const detaches = [
            subscribe.title(syncTitle),
            subscribe.muteState(syncMuteState),
        ];
        detachSession = () => {
            for (const detach of detaches) {
                detach();
            }
        };
        setSession(created);
        syncMuteState();
    };

    const detaches = [
        subscribe.remoteStream(start),
        subscribe.meetingEnded(stop),
    ];

    return Object.freeze({
        /**
         * The current session, or `undefined` while no meeting media is being
         * received.
         */
        getSession: () => session,
        /** Bind the Infinity composition that owns this media session. */
        setInfinity: (value: Infinity | undefined) => {
            infinity = value;
        },
        /**
         * Tear down the service, releasing every subscription. Only expected to
         * be used by tests.
         */
        release: () => {
            stop();
            for (const detach of detaches) {
                detach();
            }
            detaches.length = 0;
        },
    });
};

export const mediaSessionService = createMediaSessionService();

export type MediaSessionService = ReturnType<typeof createMediaSessionService>;
